"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import {
  Search, Plus, Trash2, X, ShoppingCart,
  Loader2, ArrowRight,
} from "lucide-react"
import toast from "react-hot-toast"
import { API_ENDPOINTS } from "../../../constants/config"
import { fetchVendorsList, createVendor, fetchVendorLedgerSummary } from "../../../services/ledgerService"

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`

const MultiFilter = ({ label, options, selected, onChange }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])
  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter((v) => v !== val) : [...selected, val])
  }
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`text-[11.5px] px-2.5 py-1.5 rounded-lg border cursor-pointer flex items-center gap-1 ${
          selected.length > 0
            ? "bg-blue-50 border-blue-300 text-blue-700 font-semibold"
            : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        {label}{selected.length > 0 && ` (${selected.length})`}
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && (
        <div className="absolute z-50 top-[calc(100%+4px)] left-0 bg-white border border-gray-300 rounded-lg shadow-lg min-w-[160px] max-h-48 overflow-y-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-[12px] text-gray-400">No options</div>
          ) : (
            options.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2 px-3 py-1.5 hover:bg-blue-50 cursor-pointer text-[12px] text-gray-700">
                <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} className="rounded" />
                {opt.label}
              </label>
            ))
          )}
          {selected.length > 0 && (
            <button onClick={() => onChange([])} className="w-full text-[11px] text-red-500 hover:bg-red-50 py-1.5 border-t border-gray-100 cursor-pointer bg-transparent border-x-0 border-b-0">
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const BulkPurchaseDialog = ({ open, onOpenChange, preloadItem, onSuccess }) => {
  const [cartItems, setCartItems] = useState([])
  const [itemSearch, setItemSearch] = useState("")
  const [allItems, setAllItems] = useState([])
  const [searchedItems, setSearchedItems] = useState(null)
  const [searching, setSearching] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [vendorSearch, setVendorSearch] = useState("")
  const [vendors, setVendors] = useState([])
  const [selectedVendor, setSelectedVendor] = useState(null)
  const [showVendorDrop, setShowVendorDrop] = useState(false)
  const [creatingVendor, setCreatingVendor] = useState(false)
  const [newVendorMode, setNewVendorMode] = useState(false)
  const [newVendorForm, setNewVendorForm] = useState({ name: "", contact: "", city: "", gstin: "" })

  const [paymentMode, setPaymentMode] = useState("yes")
  const [purchaseTotal, setPurchaseTotal] = useState("")
  const [purchasePaid, setPurchasePaid] = useState("")
  const [purchaseDue, setPurchaseDue] = useState("")
  const [purchaseDueDate, setPurchaseDueDate] = useState("")
  const [purchaseNotes, setPurchaseNotes] = useState("")
  const [purchaseDate, setPurchaseDate] = useState(new Date().toISOString().split("T")[0])
  const [invoiceNumber, setInvoiceNumber] = useState("")

  const [showPrice, setShowPrice] = useState({})
  const [editingQty, setEditingQty] = useState(null)
  const [filterCategory, setFilterCategory] = useState([])
  const [filterSubCategory, setFilterSubCategory] = useState([])
  const [filterBrand, setFilterBrand] = useState([])
  const [filterAvailability, setFilterAvailability] = useState([])
  const [sortCol, setSortCol] = useState(null)
  const [sortDir, setSortDir] = useState("asc")

  const [statQtyToday, setStatQtyToday] = useState(0)
  const [statPOsToday, setStatPOsToday] = useState(0)

  const [showNewItemModal, setShowNewItemModal] = useState(false)
  const [newItemForm, setNewItemForm] = useState({
    item_name: "", category: "", sub_category: "", description: "",
    quantity_purchased: 0, unit_of_measurement: "", purchase_cost: 0,
    purchase_date: "", minimum_stock_level: 0, reorder_level: 0,
    storage_location: "", brand: "", size: "", color: "",
  })
  const [newItemSubmitting, setNewItemSubmitting] = useState(false)
  const [newItemLoading, setNewItemLoading] = useState(false)
  const [refCategories, setRefCategories] = useState([])
  const [refUnits, setRefUnits] = useState([])
  const [refSizes, setRefSizes] = useState([])
  const [refBrands, setRefBrands] = useState([])
  const [refColors, setRefColors] = useState([])
  const [refSubCategories, setRefSubCategories] = useState([])
  const [isColorEnabled, setIsColorEnabled] = useState(false)
  const [isSubCategoryEnabled, setIsSubCategoryEnabled] = useState(false)
  const [brandSuggestions, setBrandSuggestions] = useState([])

  const searchInputRef = useRef(null)
  const vendorDropRef = useRef(null)
  const submittingRef = useRef(false)

  useEffect(() => {
    if (open) {
      loadAllItems()
      loadPurchaseTodayStats()
      Promise.all([fetchVendorsList(), fetchVendorLedgerSummary()]).then(([vendorRes, summaryRes]) => {
        const vends = vendorRes.data || []
        const dueMap = new Map((summaryRes.data || []).map((s) => [s._id?.toString(), s.total_due || 0]))
        setVendors(vends.map((v) => ({ ...v, total_due: dueMap.get(v._id?.toString()) || 0 })))
      }).catch(() => {})
      if (preloadItem) {
        setCartItems([{
          _id: preloadItem._id,
          inventory_id: preloadItem.inventory_id,
          item_name: preloadItem.item_name,
          sku: preloadItem.sku || `INV-${preloadItem.inventory_id}`,
          unit: preloadItem.unit_of_measurement || "pcs",
          quantity: 1,
          cost_per_unit: 0,
          brand: preloadItem.brand || "",
        }])
      }
      setTimeout(() => searchInputRef.current?.focus(), 100)
    } else {
      resetForm()
    }
  }, [open])

  const resetForm = () => {
    setCartItems([])
    setItemSearch("")
    setVendorSearch("")
    setSelectedVendor(null)
    setShowVendorDrop(false)
    setNewVendorMode(false)
    setNewVendorForm({ name: "", contact: "", city: "", gstin: "" })
    setPaymentMode("yes")
    setPurchaseTotal("")
    setPurchasePaid("")
    setPurchaseDue("")
    setPurchaseDueDate("")
    setPurchaseNotes("")
    setPurchaseDate(new Date().toISOString().split("T")[0])
    setInvoiceNumber("")
    setShowPrice({})
    setEditingQty(null)
    setFilterCategory([])
    setFilterSubCategory([])
    setFilterBrand([])
    setFilterAvailability([])
    setSortCol(null)
    setSortDir("asc")
    setShowNewItemModal(false)
    setNewItemForm({
      item_name: "", category: "", sub_category: "", description: "",
      quantity_purchased: 0, unit_of_measurement: "", purchase_cost: 0,
      purchase_date: "", minimum_stock_level: 0, reorder_level: 0,
      storage_location: "", brand: "", size: "", color: "",
    })
    setBrandSuggestions([])
  }

  const loadAllItems = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(`${API_ENDPOINTS.inventory}?limit=500&activeStatus=active&paginate=true`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      setAllItems(data.data || [])
    } catch {}
  }

  const searchInventory = async (q) => {
    const token = localStorage.getItem("token")
    const res = await fetch(
      `${API_ENDPOINTS.inventory}?search=${encodeURIComponent(q)}&activeStatus=active&limit=200&paginate=true`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
    const data = await res.json()
    return data.data || []
  }

  // Search the full catalog server-side — the initial `allItems` load is capped
  // (backend maxLimit) and won't contain every item, so filtering it client-side
  // misses matches outside that window.
  useEffect(() => {
    const q = itemSearch.trim()
    if (!q) {
      setSearchedItems(null)
      setSearching(false)
      return
    }
    setSearching(true)
    const handle = setTimeout(() => {
      searchInventory(q)
        .then((items) => setSearchedItems(items))
        .catch(() => setSearchedItems([]))
        .finally(() => setSearching(false))
    }, 250)
    return () => clearTimeout(handle)
  }, [itemSearch])

  const loadPurchaseTodayStats = async () => {
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(API_ENDPOINTS.inventoryPurchaseTodayStats, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.status === "success") {
        setStatQtyToday(data.data.qty_received_today || 0)
        setStatPOsToday(data.data.pos_today || 0)
      }
    } catch {}
  }

  const openNewItemModal = async () => {
    setShowNewItemModal(true)
    setNewItemLoading(true)
    try {
      const token = localStorage.getItem("token")
      const get = (url) => fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json())
      const [catRes, unitRes, sizeRes, brandRes, featureCfg] = await Promise.all([
        get(API_ENDPOINTS.inventoryCategories),
        get(API_ENDPOINTS.inventoryUnits),
        get(API_ENDPOINTS.inventorySizes),
        get(API_ENDPOINTS.inventoryBrands),
        get(API_ENDPOINTS.inventoryFeatureConfig),
      ])
      setRefCategories(catRes.data || [])
      setRefUnits(unitRes.data || [])
      setRefSizes(sizeRes.data || [])
      setRefBrands(brandRes.data || [])
      const colorOn = Boolean(featureCfg?.data?.features?.color)
      const subCatOn = Boolean(featureCfg?.data?.features?.sub_category)
      setIsColorEnabled(colorOn)
      setIsSubCategoryEnabled(subCatOn)
      if (colorOn) {
        const colorRes = await get(API_ENDPOINTS.inventoryColors)
        setRefColors(colorRes.data || [])
      }
    } catch {
      toast.error("Failed to load form data")
    } finally {
      setNewItemLoading(false)
    }
  }

  const loadSubCategoriesFor = async (categoryName) => {
    const cat = refCategories.find((c) => c.name === categoryName)
    if (!cat) { setRefSubCategories([]); return }
    try {
      const token = localStorage.getItem("token")
      const res = await fetch(`${API_ENDPOINTS.inventorySubCategories}?category=${cat._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      setRefSubCategories(data.data || [])
    } catch { setRefSubCategories([]) }
  }

  const setNewItem = (field, value) => setNewItemForm((prev) => ({ ...prev, [field]: value }))

  const handleNewItemSubmit = async () => {
    if (!newItemForm.item_name || !newItemForm.category) {
      return toast.error("Item name and category are required")
    }
    setNewItemSubmitting(true)
    try {
      const token = localStorage.getItem("token")
      const payload = { ...newItemForm }
      payload.quantity_available = payload.quantity_purchased
      if (!isSubCategoryEnabled) delete payload.sub_category
      if (isColorEnabled && payload.color) {
        payload.custom_attributes = { color: payload.color }
      }
      delete payload.color

      const res = await fetch(API_ENDPOINTS.inventory, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || "Failed to create item")
      const created = data.data
      const createdName = newItemForm.item_name
      if (created) addToCartByOne(created)
      setShowNewItemModal(false)
      setNewItemForm({
        item_name: "", category: "", sub_category: "", description: "",
        quantity_purchased: 0, unit_of_measurement: "", purchase_cost: 0,
        purchase_date: "", minimum_stock_level: 0, reorder_level: 0,
        storage_location: "", brand: "", size: "", color: "",
      })
      loadAllItems()
      toast.success(`"${createdName}" added to catalog & basket`)
    } catch (e) {
      toast.error(e.message || "Failed to create item")
    } finally {
      setNewItemSubmitting(false)
    }
  }

  const addToCartByOne = (item) => {
    const existing = cartItems.find((c) => c.inventory_id === item.inventory_id)
    if (existing) {
      setCartItems((prev) =>
        prev.map((c) => (c.inventory_id === item.inventory_id ? { ...c, quantity: c.quantity + 1 } : c))
      )
    } else {
      setCartItems((prev) => [
        ...prev,
        {
          _id: item._id,
          inventory_id: item.inventory_id,
          item_name: item.item_name,
          sku: item.sku || `INV-${item.inventory_id}`,
          unit: item.unit_of_measurement || "pcs",
          quantity: 1,
          cost_per_unit: 0,
          brand: item.brand || "",
        },
      ])
    }
  }

  const changeCartQty = (inventoryId, delta) => {
    setCartItems((prev) => {
      const item = prev.find((c) => c.inventory_id === inventoryId)
      if (!item) return prev
      const next = item.quantity + delta
      if (next <= 0) return prev.filter((c) => c.inventory_id !== inventoryId)
      return prev.map((c) => (c.inventory_id === inventoryId ? { ...c, quantity: next } : c))
    })
  }

  const commitCartQty = (inventoryId) => {
    if (!editingQty || editingQty.id !== inventoryId) return
    const num = parseInt(editingQty.value, 10)
    setEditingQty(null)
    if (isNaN(num) || num <= 0) return
    setCartItems((prev) =>
      prev.map((c) => (c.inventory_id === inventoryId ? { ...c, quantity: num } : c))
    )
  }

  const removeCartItem = (inventoryId) => {
    setCartItems((prev) => prev.filter((c) => c.inventory_id !== inventoryId))
    setShowPrice((prev) => { const n = { ...prev }; delete n[inventoryId]; return n })
  }

  const updateItemPrice = (inventoryId, value) => {
    setCartItems((prev) =>
      prev.map((c) => c.inventory_id === inventoryId ? { ...c, cost_per_unit: Math.max(0, parseFloat(value) || 0) } : c)
    )
  }

  const totals = useMemo(() => {
    const totalItems = cartItems.length
    const totalQty = cartItems.reduce((s, c) => s + (c.quantity || 0), 0)
    const totalCost = cartItems.reduce((s, c) => s + (c.quantity || 0) * (c.cost_per_unit || 0), 0)
    const hasPrices = cartItems.some((c) => c.cost_per_unit > 0)
    return { totalItems, totalQty, totalCost, hasPrices }
  }, [cartItems])

  const filterOptions = useMemo(() => {
    const cats = [...new Set(allItems.map((i) => i.category).filter(Boolean))].sort()
    const subs = [...new Set(allItems.map((i) => i.sub_category).filter(Boolean))].sort()
    const brands = [...new Set(allItems.map((i) => i.brand).filter(Boolean))].sort()
    return { cats, subs, brands }
  }, [allItems])

  const applyLocalFilters = useCallback((items) => {
    let filtered = items
    if (filterCategory.length > 0) filtered = filtered.filter((i) => filterCategory.includes(i.category))
    if (filterSubCategory.length > 0) filtered = filtered.filter((i) => filterSubCategory.includes(i.sub_category))
    if (filterBrand.length > 0) filtered = filtered.filter((i) => filterBrand.includes(i.brand))
    if (filterAvailability.length > 0) {
      filtered = filtered.filter((i) => {
        const avail = i.quantity_available || 0
        if (avail <= 0) return filterAvailability.includes("out")
        if (avail <= 10) return filterAvailability.includes("low")
        return filterAvailability.includes("available")
      })
    }
    return filtered
  }, [filterCategory, filterSubCategory, filterBrand, filterAvailability])

  const displayItems = useMemo(() => {
    const base = itemSearch.trim() ? (searchedItems || []) : allItems
    let filtered = applyLocalFilters(base)
    if (sortCol) {
      filtered = [...filtered].sort((a, b) => {
        let va = "", vb = ""
        if (sortCol === "item") { va = a.item_name || ""; vb = b.item_name || "" }
        else if (sortCol === "category") { va = a.category || ""; vb = b.category || "" }
        else if (sortCol === "sub_category") { va = a.sub_category || ""; vb = b.sub_category || "" }
        const cmp = va.localeCompare(vb, "en", { sensitivity: "base" })
        return sortDir === "asc" ? cmp : -cmp
      })
    }
    return filtered.slice(0, 30)
  }, [itemSearch, allItems, searchedItems, applyLocalFilters, sortCol, sortDir])

  const handleSearchKeyDown = async (e) => {
    if (e.key !== "Enter") return
    const q = itemSearch.trim()
    if (!q) return
    e.preventDefault()
    try {
      const items = !searching && searchedItems ? searchedItems : await searchInventory(q)
      const match = applyLocalFilters(items)[0]
      if (match) {
        addToCartByOne(match)
        e.target.select()
      } else {
        toast.error("No matching item found.")
      }
    } catch {
      toast.error("Search failed.")
    }
  }

  const filteredVendors = useMemo(() => {
    if (!vendorSearch.trim()) return vendors.slice(0, 5)
    const q = vendorSearch.toLowerCase()
    return vendors.filter((v) => v.name?.toLowerCase().includes(q) || v.city?.toLowerCase().includes(q)).slice(0, 5)
  }, [vendors, vendorSearch])

  const handleCreateVendor = async () => {
    if (!vendorSearch.trim()) return
    setCreatingVendor(true)
    try {
      const res = await createVendor({ name: vendorSearch.trim() })
      const newVendor = res.data
      setVendors((prev) => [newVendor, ...prev])
      setSelectedVendor(newVendor)
      setVendorSearch(newVendor.name)
      setShowVendorDrop(false)
      toast.success("Vendor created")
    } catch {
      toast.error("Failed to create vendor")
    } finally {
      setCreatingVendor(false)
    }
  }

  const saveNewVendor = async () => {
    if (!newVendorForm.name.trim() || !newVendorForm.contact.trim()) return
    setCreatingVendor(true)
    try {
      const res = await createVendor({
        name: newVendorForm.name.trim(),
        contactDetails: { phone: newVendorForm.contact.trim() },
      })
      const newVendor = res.data
      setVendors((prev) => [newVendor, ...prev])
      setSelectedVendor(newVendor)
      setVendorSearch(newVendor.name)
      setNewVendorMode(false)
      setNewVendorForm({ name: "", contact: "", city: "", gstin: "" })
      toast.success("Vendor created")
    } catch {
      toast.error("Failed to create vendor")
    } finally {
      setCreatingVendor(false)
    }
  }

  const selectVendor = (v) => {
    setSelectedVendor(v)
    setVendorSearch(v.name)
    setShowVendorDrop(false)
  }

  useEffect(() => {
    const total = parseFloat(purchaseTotal) || 0
    const paid = parseFloat(purchasePaid) || 0
    if (paymentMode === "yes") setPurchaseDue("0")
    else if (paymentMode === "partial") setPurchaseDue(Math.max(total - paid, 0).toString())
    else if (paymentMode === "no") setPurchaseDue(total.toString())
    else setPurchaseDue("")
  }, [purchaseTotal, purchasePaid, paymentMode])

  const dueNoteContent = useMemo(() => {
    const bill = parseFloat(purchaseTotal) || 0
    const received = parseFloat(purchasePaid) || 0
    const vendorName = selectedVendor?.name || vendorSearch.trim() || "vendor"
    if (paymentMode === "yes") {
      if (bill > 0) return { text: `Paid ${money(bill)} · No outstanding with ${vendorName}`, type: "success" }
      return { text: "No outstanding — fully settled.", type: "success" }
    }
    if (paymentMode === "partial") {
      if (bill <= 0) return { text: "Enter the bill amount to calculate outstanding.", type: "neutral" }
      const due = Math.max(bill - received, 0)
      return due > 0
        ? { text: `Paid ${money(received)} · ${money(due)} outstanding will be added to ${vendorName}'s ledger`, type: "warn" }
        : { text: `Paid ${money(received)} · No outstanding`, type: "success" }
    }
    if (paymentMode === "no") {
      if (bill <= 0) return { text: "Enter the bill amount to log outstanding with vendor.", type: "neutral" }
      return { text: `${money(bill)} will be added as outstanding to ${vendorName}'s ledger`, type: "error" }
    }
    return { text: "", type: "neutral" }
  }, [paymentMode, purchaseTotal, purchasePaid, selectedVendor, vendorSearch])

  const handleSubmit = async () => {
    if (submittingRef.current) return
    if (cartItems.length === 0) return toast.error("Add at least one item")
    if (cartItems.some((c) => c.quantity <= 0)) return toast.error("All quantities must be > 0")

    if (paymentMode && paymentMode !== "yes") {
      const total = parseFloat(purchaseTotal) || 0
      if (total === 0) return toast.error("Enter total amount")
      if (!selectedVendor) return toast.error("Vendor is required for partial/unpaid payments")
    }

    submittingRef.current = true
    setSubmitting(true)
    try {
      const token = localStorage.getItem("token")
      const total = parseFloat(purchaseTotal) || 0
      let paid = 0
      if (paymentMode === "yes") paid = total
      else if (paymentMode === "partial") paid = parseFloat(purchasePaid) || 0

      const body = {
        items: cartItems.map((c) => ({
          inventory_id: c.inventory_id,
          quantity: c.quantity,
          cost_per_unit: c.cost_per_unit || 0,
          brand: c.brand || "",
        })),
        vendor_id: selectedVendor?._id || undefined,
        vendor_name: selectedVendor?.name || vendorSearch.trim() || "",
        purchase_date: purchaseDate,
        remarks: purchaseNotes,
        invoice_number: invoiceNumber,
        total_amount: total,
        paid_amount: paid,
        due_date: purchaseDueDate || undefined,
      }

      const res = await fetch(API_ENDPOINTS.inventoryBulkPurchase, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.message || "Failed to purchase")

      const { successful, failed } = data.results
      if (successful.length > 0) toast.success(`${successful.length} item(s) purchased successfully`)
      if (failed.length > 0) toast.error(`${failed.length} item(s) failed: ${failed.map((f) => f.reason).join(", ")}`)
      loadPurchaseTodayStats()
      onSuccess?.()
      onOpenChange(false)
    } catch (e) {
      toast.error(e.message || "Failed to process purchase")
    } finally {
      setSubmitting(false)
      submittingRef.current = false
    }
  }

  useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.ctrlKey && e.key === "Enter") {
        e.preventDefault()
        handleSubmit()
      }
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [open, handleSubmit])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1280px] w-[95vw] max-h-[92vh] overflow-hidden p-0 gap-0 [&>button.absolute]:hidden">
        {/* ── New Item Modal ── */}
        {showNewItemModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-[999]">
            <div className="bg-white rounded-xl w-[600px] max-h-[88vh] overflow-auto shadow-2xl">
              <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                <div>
                  <p className="font-bold text-[15px]">Add New Item</p>
                  <p className="text-[12px] text-gray-400 mt-0.5">Fill in the details to add a new inventory item</p>
                </div>
                <button onClick={() => setShowNewItemModal(false)} className="text-gray-400 hover:text-gray-600 text-[22px] leading-none bg-transparent border-none cursor-pointer">×</button>
              </div>

              {newItemLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-5 w-5 animate-spin text-blue-600 mr-2" />
                  <span className="text-sm text-gray-500">Loading…</span>
                </div>
              ) : (
                <div className="px-6 py-5 space-y-5">
                  {/* Basic Info */}
                  <div>
                    <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-1.5 border-b border-gray-100">Basic Information</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Item Name <span className="text-red-500">*</span></label>
                        <input value={newItemForm.item_name} onChange={(e) => setNewItem("item_name", e.target.value)} placeholder="e.g. A4 Paper Ream"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Category <span className="text-red-500">*</span></label>
                        <select value={newItemForm.category} onChange={(e) => { setNewItem("category", e.target.value); setNewItem("sub_category", ""); if (isSubCategoryEnabled) loadSubCategoriesFor(e.target.value) }}
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] bg-white outline-none focus:border-blue-500">
                          <option value="">Select category</option>
                          {refCategories.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
                        </select>
                      </div>
                      {isSubCategoryEnabled && (
                        <div>
                          <label className="text-[12px] text-gray-600 block mb-1">Sub-category</label>
                          <select value={newItemForm.sub_category} onChange={(e) => setNewItem("sub_category", e.target.value)} disabled={!newItemForm.category}
                            className={`w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] bg-white outline-none focus:border-blue-500 ${!newItemForm.category ? "opacity-50" : ""}`}>
                            <option value="">Select sub-category</option>
                            {refSubCategories.map((s) => <option key={s._id} value={s.name}>{s.name}</option>)}
                          </select>
                        </div>
                      )}
                      <div className="col-span-2">
                        <label className="text-[12px] text-gray-600 block mb-1">Description</label>
                        <input value={newItemForm.description} onChange={(e) => setNewItem("description", e.target.value)} placeholder="Optional description"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                    </div>
                  </div>

                  {/* Stock & Pricing */}
                  <div>
                    <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-1.5 border-b border-gray-100">Stock & Pricing</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Initial Quantity</label>
                        <input type="number" step="any" value={newItemForm.quantity_purchased} onChange={(e) => setNewItem("quantity_purchased", parseFloat(e.target.value) || 0)} onFocus={(e) => e.target.select()} placeholder="0"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Unit of Measurement</label>
                        <select value={newItemForm.unit_of_measurement} onChange={(e) => setNewItem("unit_of_measurement", e.target.value)}
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] bg-white outline-none focus:border-blue-500">
                          <option value="">Select unit</option>
                          {refUnits.map((u) => <option key={u._id} value={u.name}>{u.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Purchase Cost (₹)</label>
                        <input type="number" value={newItemForm.purchase_cost} onChange={(e) => setNewItem("purchase_cost", parseFloat(e.target.value) || 0)} onFocus={(e) => e.target.select()} placeholder="0"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Purchase Date</label>
                        <input type="date" value={newItemForm.purchase_date} onChange={(e) => setNewItem("purchase_date", e.target.value)}
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Minimum Stock Level</label>
                        <input type="number" step="any" value={newItemForm.minimum_stock_level} onChange={(e) => setNewItem("minimum_stock_level", parseFloat(e.target.value) || 0)} onFocus={(e) => e.target.select()} placeholder="0"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Reorder Level</label>
                        <input type="number" step="any" value={newItemForm.reorder_level} onChange={(e) => setNewItem("reorder_level", parseFloat(e.target.value) || 0)} onFocus={(e) => e.target.select()} placeholder="0"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[12px] text-gray-600 block mb-1">Storage Location</label>
                        <input value={newItemForm.storage_location} onChange={(e) => setNewItem("storage_location", e.target.value)} placeholder="e.g. Shelf A3, Storeroom 2"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                      </div>
                    </div>
                  </div>

                  {/* Attributes */}
                  <div>
                    <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-3 pb-1.5 border-b border-gray-100">Attributes</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="relative">
                        <label className="text-[12px] text-gray-600 block mb-1">Brand</label>
                        <input value={newItemForm.brand} onChange={(e) => { setNewItem("brand", e.target.value); setBrandSuggestions(e.target.value ? refBrands.filter((b) => b.name.toLowerCase().startsWith(e.target.value.toLowerCase())).slice(0, 8) : []) }}
                          onBlur={() => setTimeout(() => setBrandSuggestions([]), 150)}
                          placeholder="Type to search brands" autoComplete="off"
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] outline-none focus:border-blue-500" />
                        {brandSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {brandSuggestions.map((b) => (
                              <button key={b._id} type="button" className="w-full text-left px-3 py-2 text-[12px] hover:bg-gray-50 border-none bg-transparent cursor-pointer"
                                onMouseDown={() => { setNewItem("brand", b.name); setBrandSuggestions([]) }}>{b.name}</button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-[12px] text-gray-600 block mb-1">Size</label>
                        <select value={newItemForm.size} onChange={(e) => setNewItem("size", e.target.value)}
                          className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] bg-white outline-none focus:border-blue-500">
                          <option value="">None</option>
                          {refSizes.map((s) => <option key={s._id} value={s.name}>{s.name}</option>)}
                        </select>
                      </div>
                      {isColorEnabled && (
                        <div>
                          <label className="text-[12px] text-gray-600 block mb-1">Color</label>
                          <select value={newItemForm.color} onChange={(e) => setNewItem("color", e.target.value)}
                            className="w-full px-2.5 py-2 border-[1.5px] border-gray-200 rounded-[7px] text-[13px] bg-white outline-none focus:border-blue-500">
                            <option value="">None</option>
                            {refColors.map((c) => <option key={c._id} value={c.name}>{c.name}</option>)}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2.5 pt-2">
                    <button onClick={handleNewItemSubmit} disabled={!newItemForm.item_name || !newItemForm.category || newItemSubmitting}
                      className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold border-none cursor-pointer flex items-center justify-center gap-2 ${
                        newItemForm.item_name && newItemForm.category && !newItemSubmitting ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-400 cursor-not-allowed"
                      }`}>
                      {newItemSubmitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                      {newItemSubmitting ? "Adding..." : "Add Item & Add to Basket"}
                    </button>
                    <button onClick={() => setShowNewItemModal(false)} className="px-4 py-2.5 bg-white border-[1.5px] border-gray-200 rounded-lg text-[13px] cursor-pointer text-gray-600">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-3 border-b bg-white flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-extrabold text-gray-900 tracking-tight flex items-center gap-2">
              <span className="bg-blue-50 text-blue-600 text-[11px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide">Purchase</span>
              Bulk Purchase · Goods Receipt
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Add items, tag the vendor, log payment — receive everything in one click.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-500 flex items-center gap-1.5">
              QTY received today <b className="font-mono font-semibold text-gray-900">{statQtyToday}</b>
            </span>
            <span className="bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs text-gray-500 flex items-center gap-1.5">
              POs today <b className="font-mono font-semibold text-gray-900">{statPOsToday}</b>
            </span>
            <button onClick={() => onOpenChange(false)} className="text-gray-400 hover:text-gray-600 p-1 ml-1">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* ── Two-column layout ── */}
        <div className="flex overflow-hidden" style={{ height: "calc(92vh - 76px)" }}>
          {/* ── LEFT PANEL — Catalog ── */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#F5F6F9]">
            {/* Search */}
            <div className="bg-white border border-gray-200 rounded-[10px] p-4">
              <div className="text-[13px] font-bold text-gray-900 mb-0.5">Find an item</div>
              <div className="text-xs text-gray-500 mb-3">Scan a barcode, or type and press Enter to add the top match instantly.</div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by item name, SKU, brand, or category…"
                  autoComplete="off"
                  className="w-full pl-9 pr-4 py-3 border-[1.5px] border-gray-300 rounded-[9px] text-sm outline-none transition-all focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>
              <p className="text-[11.5px] text-gray-400 mt-1.5">
                Tip: <kbd className="bg-gray-100 border border-gray-300 rounded px-1.5 py-px font-mono text-[10.5px] text-gray-500">Enter</kbd>{" "}
                adds the first matching result with qty 1, no mouse needed.
              </p>
            </div>

            {/* All items table */}
            <div className="bg-white border border-gray-200 rounded-[10px] p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[13px] font-bold text-gray-900 mb-0.5">All items</div>
                  <div className="text-xs text-gray-500">
                    {itemSearch.trim()
                      ? searching
                        ? "Searching…"
                        : `${displayItems.length} result(s) for "${itemSearch}"`
                      : `Showing catalog (${allItems.length} items)`}
                  </div>
                </div>
                <button
                  onClick={openNewItemModal}
                  className="border-[1.5px] border-blue-500 text-blue-500 bg-white hover:bg-blue-500 hover:text-white font-semibold text-[12px] px-3 py-1.5 rounded-[7px] transition-all flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" /> New item
                </button>
              </div>

              {/* Filters */}
              <div className="flex gap-2 mb-3 flex-wrap">
                <MultiFilter label="Category" options={filterOptions.cats.map((c) => ({ value: c, label: c }))} selected={filterCategory} onChange={setFilterCategory} />
                <MultiFilter label="Sub Category" options={filterOptions.subs.map((s) => ({ value: s, label: s }))} selected={filterSubCategory} onChange={setFilterSubCategory} />
                <MultiFilter label="Brand" options={filterOptions.brands.map((b) => ({ value: b, label: b }))} selected={filterBrand} onChange={setFilterBrand} />
                <MultiFilter
                  label="Stock"
                  options={[
                    { value: "available", label: "In stock" },
                    { value: "low", label: "Low stock" },
                    { value: "out", label: "Out of stock" },
                  ]}
                  selected={filterAvailability}
                  onChange={setFilterAvailability}
                />
              </div>

              <table className="w-full text-[13px]">
                <thead>
                  <tr>
                    {[
                      { key: "item", label: "Item", sortable: true },
                      { key: "brand", label: "Brand", sortable: false },
                      { key: "category", label: "Category", sortable: true },
                      { key: "stock", label: "Current Stock", sortable: false },
                      { key: "action", label: "", sortable: false },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={col.sortable ? () => {
                          if (sortCol === col.key) setSortDir(sortDir === "asc" ? "desc" : "asc")
                          else { setSortCol(col.key); setSortDir("asc") }
                        } : undefined}
                        className={`text-left text-[11px] uppercase tracking-wider text-gray-400 font-semibold pb-2 border-b border-gray-200 ${
                          col.key === "item" ? "pl-2" : col.key === "action" ? "text-right pr-2" : ""
                        } ${col.sortable ? "cursor-pointer hover:text-gray-600 select-none" : ""}`}
                      >
                        {col.label}
                        {col.sortable && sortCol === col.key && (
                          <span className="ml-1 text-blue-500">{sortDir === "asc" ? "↑" : "↓"}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center text-gray-400 py-5 text-[13px]">
                        No items match.{" "}
                        <span onClick={openNewItemModal} className="text-blue-600 cursor-pointer font-medium hover:underline">Add as new item →</span>
                      </td>
                    </tr>
                  ) : (
                    displayItems.map((item) => {
                      const avail = item.quantity_available || 0
                      const out = avail <= 0
                      const low = avail <= 10
                      const inBasket = cartItems.some((c) => c.inventory_id === item.inventory_id)
                      return (
                        <tr key={item.inventory_id} className="hover:bg-[#FAFBFD] border-b border-gray-200">
                          <td className="py-2.5 pl-2">
                            <div className="font-semibold text-gray-900">{item.item_name}</div>
                            <div className="text-[11px] font-mono text-gray-400 mt-px">
                              INV-{item.inventory_id}{item.brand ? ` · ${item.brand}` : ""}
                            </div>
                          </td>
                          <td className="py-2.5 text-gray-500">{item.brand || "—"}</td>
                          <td className="py-2.5">
                            <div className="text-gray-700">{item.category || "—"}</div>
                            {item.sub_category && <div className="text-[11px] text-gray-400">{item.sub_category}</div>}
                          </td>
                          <td className="py-2.5">
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md inline-block ${
                              out ? "bg-red-50 text-red-600" : low ? "bg-amber-50 text-amber-700" : "bg-green-50 text-green-700"
                            }`}>
                              {out ? "Out of stock" : low ? `Low stock · ${avail}` : `In stock · ${avail}`}
                            </span>
                          </td>
                          <td className="py-2.5 text-right pr-2">
                            {inBasket ? (
                              <span className="bg-blue-50 text-blue-600 text-[11px] font-semibold px-2.5 py-1 rounded-md">In basket</span>
                            ) : (
                              <button
                                onClick={() => addToCartByOne(item)}
                                className="border-[1.5px] font-bold text-[12px] px-3 py-1.5 rounded-[7px] transition-all border-gray-300 text-gray-700 bg-white hover:border-blue-500 hover:text-blue-500"
                              >
                                Add
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── RIGHT PANEL — Basket ── */}
          <div className="w-[390px] border-l border-gray-200 bg-white overflow-hidden flex flex-col flex-shrink-0">
            {/* Basket header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <h2 className="text-[15px] font-extrabold text-gray-900">Basket</h2>
                <span className={`text-[11.5px] font-bold font-mono px-2.5 py-0.5 rounded-full ${
                  totals.totalQty > 0 ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-400"
                }`}>
                  {totals.totalQty} item{totals.totalQty !== 1 ? "s" : ""}
                </span>
              </div>
              {cartItems.length > 0 && (
                <button
                  onClick={() => { setCartItems([]); setShowPrice({}) }}
                  className="text-[12px] text-red-500 hover:text-red-600 bg-transparent border-none p-0 cursor-pointer font-medium"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* ── Vendor ── */}
              <div className="mb-3.5">
                <label className="text-[11.5px] font-bold text-gray-500 uppercase tracking-wide block mb-1.5">
                  Vendor{" "}
                  {(paymentMode === "partial" || paymentMode === "no") && <span className="text-red-500 font-extrabold">*</span>}
                </label>

                {!newVendorMode ? (
                  <div className="relative" ref={vendorDropRef}>
                    <input
                      type="text"
                      placeholder="Search or type a vendor name…"
                      autoComplete="off"
                      className="w-full px-3 py-2 border-[1.5px] border-gray-300 rounded-lg text-[13.5px] outline-none transition-all focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                      value={selectedVendor ? selectedVendor.name : vendorSearch}
                      onChange={(e) => {
                        setVendorSearch(e.target.value)
                        setSelectedVendor(null)
                        setShowVendorDrop(true)
                      }}
                      onFocus={() => setShowVendorDrop(true)}
                      onBlur={() => setTimeout(() => setShowVendorDrop(false), 150)}
                    />
                    {selectedVendor && (
                      <button
                        onClick={() => { setSelectedVendor(null); setVendorSearch("") }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                      >
                        ✕
                      </button>
                    )}
                    {showVendorDrop && !selectedVendor && (
                      <div className="absolute z-50 left-0 right-0 top-[calc(100%+4px)] bg-white border border-gray-300 rounded-[9px] shadow-lg max-h-48 overflow-y-auto">
                        {filteredVendors.map((v) => (
                          <div
                            key={v._id}
                            className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-blue-50 text-[13px]"
                            onMouseDown={() => selectVendor(v)}
                          >
                            <div>
                              <div className="font-semibold text-gray-900">{v.name}</div>
                              {v.contactDetails?.phone && <div className="text-[11px] text-gray-400">{v.contactDetails.phone}</div>}
                            </div>
                            {(v.total_due > 0 || v.balance > 0) && (
                              <span className="font-mono text-[11px] text-red-600 bg-red-50 px-2 py-0.5 rounded-md">
                                {money(v.total_due || v.balance)} due
                              </span>
                            )}
                          </div>
                        ))}
                        {vendorSearch.trim() && !vendors.some((v) => v.name?.toLowerCase() === vendorSearch.trim().toLowerCase()) && (
                          <div
                            className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-blue-600 font-semibold text-[13px] border-t border-gray-100"
                            onMouseDown={handleCreateVendor}
                          >
                            <Plus className="w-3.5 h-3.5" />
                            {creatingVendor ? "Creating..." : `Add "${vendorSearch.trim()}" as new vendor`}
                          </div>
                        )}
                        <div
                          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-blue-50 text-blue-600 font-semibold text-[13px] border-t border-gray-100"
                          onMouseDown={() => { setNewVendorMode(true); setShowVendorDrop(false) }}
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add new vendor with details
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border-[1.5px] border-blue-200 rounded-lg p-3 bg-blue-50/30">
                    <p className="text-[12px] font-bold text-blue-800 mb-2.5">New vendor</p>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <label className="text-[11px] text-blue-700 block mb-1 font-medium">Vendor name *</label>
                        <input
                          value={newVendorForm.name}
                          onChange={(e) => setNewVendorForm((f) => ({ ...f, name: e.target.value }))}
                          placeholder="e.g., Gupta Traders"
                          className="w-full px-2 py-1.5 border-[1.5px] border-blue-200 rounded-md text-[12px] outline-none focus:border-blue-500 bg-white"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] text-blue-700 block mb-1 font-medium">Contact *</label>
                        <input
                          value={newVendorForm.contact}
                          onChange={(e) => setNewVendorForm((f) => ({ ...f, contact: e.target.value }))}
                          placeholder="10-digit mobile"
                          className="w-full px-2 py-1.5 border-[1.5px] border-blue-200 rounded-md text-[12px] outline-none focus:border-blue-500 bg-white"
                        />
                      </div>
                    </div>
                    <p className="text-[10.5px] text-blue-500 mb-2">You can update other details later from Vendor Management.</p>
                    <div className="flex gap-2 mt-1">
                      <button
                        onClick={saveNewVendor}
                        disabled={!newVendorForm.name.trim() || !newVendorForm.contact.trim() || creatingVendor}
                        className={`flex-1 py-1.5 rounded-md text-[12px] font-semibold border-none cursor-pointer ${
                          newVendorForm.name.trim() && newVendorForm.contact.trim() && !creatingVendor
                            ? "bg-blue-600 text-white"
                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                        }`}
                      >
                        {creatingVendor ? "Saving..." : "Save vendor"}
                      </button>
                      <button
                        onClick={() => { setNewVendorMode(false); setNewVendorForm({ name: "", contact: "", city: "", gstin: "" }) }}
                        className="px-3 py-1.5 bg-white border-[1.5px] border-blue-200 rounded-md text-[12px] text-gray-600 cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {selectedVendor && (
                  <div className={`mt-2 text-[12px] px-2.5 py-1.5 rounded-[7px] flex justify-between items-center ${
                    (selectedVendor.total_due > 0 || selectedVendor.balance > 0)
                      ? "bg-amber-50 border border-amber-200 text-amber-800"
                      : "bg-green-50 border border-green-200 text-green-700"
                  }`}>
                    <div>
                      <span className="font-bold">{selectedVendor.name}</span>
                      {selectedVendor.city && <span className="text-[11px] ml-1">· {selectedVendor.city}</span>}
                    </div>
                    {(selectedVendor.total_due > 0 || selectedVendor.balance > 0)
                      ? <span className="font-mono font-semibold">Outstanding: {money(selectedVendor.total_due || selectedVendor.balance)}</span>
                      : <span className="text-[11px] font-medium">✓ No outstanding</span>
                    }
                  </div>
                )}

                {!selectedVendor && !newVendorMode && vendors.some((v) => (v.total_due > 0 || v.balance > 0)) && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {vendors.filter((v) => (v.total_due > 0 || v.balance > 0)).slice(0, 4).map((v) => (
                      <button
                        key={v._id}
                        className="text-[11.5px] bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1 text-gray-500 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-100 cursor-pointer"
                        onMouseDown={() => selectVendor(v)}
                      >
                        {v.name} · <span className="text-red-500 font-medium">{money(v.total_due || v.balance)} due</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-dashed border-gray-300 my-3.5" />

              {/* ── Cart items ── */}
              <div className="min-h-[60px] max-h-[240px] overflow-y-auto">
                {cartItems.length === 0 ? (
                  <div className="text-center py-8 text-gray-400 text-[13px]">
                    <ShoppingCart className="h-8 w-8 mx-auto mb-2 text-gray-300" strokeWidth={1.6} />
                    Basket is empty.
                    <br />
                    Add items from the left.
                  </div>
                ) : (
                  cartItems.map((c) => (
                    <div key={c.inventory_id} className="mb-3 pb-3 border-b border-gray-200 last:border-0">
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-gray-900 truncate">{c.item_name}</div>
                          <div className="text-[11px] font-mono text-gray-400 truncate">{c.sku}</div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <div className="flex items-center border-[1.5px] border-gray-300 rounded-[7px] overflow-hidden">
                            <button
                              onClick={() => changeCartQty(c.inventory_id, -1)}
                              className="w-6 h-[26px] flex items-center justify-center bg-gray-50 hover:bg-blue-50 hover:text-blue-600 font-bold text-sm border-none cursor-pointer"
                            >
                              −
                            </button>
                            <input
                              value={editingQty?.id === c.inventory_id ? editingQty.value : c.quantity}
                              onChange={(e) => {
                                const v = e.target.value.replace(/[^0-9]/g, "").replace(/^0+/, "")
                                setEditingQty({ id: c.inventory_id, value: v })
                              }}
                              onFocus={(e) => {
                                setEditingQty({ id: c.inventory_id, value: String(c.quantity) })
                                setTimeout(() => e.target.select(), 0)
                              }}
                              onBlur={() => commitCartQty(c.inventory_id)}
                              onKeyDown={(e) => { if (e.key === "Enter") e.target.blur() }}
                              inputMode="numeric"
                              className="w-8 text-center font-mono text-xs font-semibold border-none outline-none py-1"
                            />
                            <button
                              onClick={() => changeCartQty(c.inventory_id, 1)}
                              className="w-6 h-[26px] flex items-center justify-center bg-gray-50 hover:bg-blue-50 hover:text-blue-600 font-bold text-sm border-none cursor-pointer"
                            >
                              +
                            </button>
                          </div>
                          <button
                            onClick={() => removeCartItem(c.inventory_id)}
                            className="text-gray-300 hover:text-red-500 p-1 border-none bg-transparent cursor-pointer"
                          >
                            <Trash2 className="h-[15px] w-[15px]" />
                          </button>
                        </div>
                      </div>
                      {/* Unit price tracking */}
                      <div className="mt-1.5">
                        {!showPrice[c.inventory_id] ? (
                          <button
                            onClick={() => {
                              if (!selectedVendor) return toast.error("Select a vendor first to track unit price")
                              setShowPrice((s) => ({ ...s, [c.inventory_id]: true }))
                            }}
                            className="text-[11px] text-gray-400 bg-transparent border-none cursor-pointer p-0 hover:text-blue-500"
                          >
                            + Track unit price for vendor comparison
                          </button>
                        ) : (
                          <div className="flex items-center gap-1.5 bg-gray-50 rounded-md px-2 py-1.5">
                            <span className="text-[11px] text-gray-500 flex-shrink-0">₹/{c.unit}</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={c.cost_per_unit || ""}
                              onChange={(e) => updateItemPrice(c.inventory_id, e.target.value)}
                              onFocus={(e) => e.target.select()}
                              placeholder="Unit price"
                              className="w-20 px-1.5 py-0.5 border-[1.5px] border-gray-200 rounded-[5px] text-[12px] outline-none focus:border-blue-500"
                            />
                            {c.cost_per_unit > 0 && c.quantity > 0 && (
                              <span className="text-[11px] font-semibold text-gray-700">= {money(Math.round(c.quantity * c.cost_per_unit))}</span>
                            )}
                            <button
                              onClick={() => {
                                setShowPrice((s) => ({ ...s, [c.inventory_id]: false }))
                                updateItemPrice(c.inventory_id, 0)
                              }}
                              className="ml-auto text-[12px] text-gray-400 bg-transparent border-none cursor-pointer p-0 hover:text-gray-600"
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Totals */}
              {cartItems.length > 0 && (
                <div className="border-t-[1.5px] border-gray-200 pt-2 mt-1">
                  <div className="flex justify-between text-[13px] text-gray-500 mb-1">
                    <span>Items</span>
                    <span className="font-semibold text-gray-800">{totals.totalItems} SKU{totals.totalItems > 1 ? "s" : ""} · {totals.totalQty} units</span>
                  </div>
                  {totals.hasPrices && (
                    <div className="flex justify-between text-[12px] text-gray-500">
                      <span>Estimated value</span>
                      <span className="font-bold text-gray-900">{money(Math.round(totals.totalCost))}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-dashed border-gray-300 my-3.5" />

              {/* ── Payment ── */}
              <div>
                <label className="text-[11.5px] font-bold text-gray-500 uppercase tracking-wide block mb-1.5">Payment</label>
                <div className="flex border-[1.5px] border-gray-300 rounded-lg overflow-hidden mb-3">
                  {[
                    { value: "yes", label: "Received in full" },
                    { value: "partial", label: "Partial" },
                    { value: "no", label: "Unpaid (Credit)" },
                  ].map((opt, idx) => (
                    <button
                      key={opt.value}
                      onClick={() => setPaymentMode(opt.value)}
                      className={`flex-1 py-2.5 text-[12px] font-bold transition-all cursor-pointer border-none ${
                        paymentMode === opt.value
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-700"
                      }`}
                      style={idx < 2 ? { borderRight: "1px solid #D2D7E2" } : {}}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                <div className="mb-2.5">
                  <label className="text-[11.5px] font-semibold text-gray-500 block mb-1.5">
                    Bill amount{paymentMode !== "yes" && <span className="text-red-500 font-extrabold ml-0.5">*</span>}
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-2.5 py-2 border-[1.5px] border-gray-300 rounded-[7px] text-[13.5px] font-mono outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                    placeholder={totals.hasPrices ? `${Math.round(totals.totalCost)} (from prices above)` : paymentMode === "yes" ? "Optional — for your records" : "Required for vendor ledger"}
                    value={purchaseTotal}
                    onChange={(e) => setPurchaseTotal(e.target.value)}
                    onFocus={(e) => e.target.select()}
                  />
                </div>

                {paymentMode === "partial" && (
                  <div className="mb-2.5">
                    <label className="text-[11.5px] font-semibold text-gray-500 block mb-1.5">Amount paid now</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full px-2.5 py-2 border-[1.5px] border-gray-300 rounded-[7px] text-[13.5px] font-mono outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                      placeholder="0"
                      value={purchasePaid}
                      onChange={(e) => setPurchasePaid(e.target.value)}
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                )}

                {(paymentMode === "partial" || paymentMode === "no") && (
                  <div className="mb-2.5">
                    <label className="text-[11.5px] font-semibold text-gray-500 block mb-1.5">Due date</label>
                    <input
                      type="date"
                      className="w-full px-2.5 py-2 border-[1.5px] border-gray-300 rounded-[7px] text-[13.5px] outline-none focus:border-blue-500 focus:shadow-[0_0_0_3px_#EAEFFF]"
                      value={purchaseDueDate}
                      onChange={(e) => {
                        const today = new Date().toISOString().split("T")[0]
                        if (e.target.value && e.target.value < today) {
                          toast.error("Due date cannot be in the past")
                          setPurchaseDueDate("")
                          return
                        }
                        setPurchaseDueDate(e.target.value)
                      }}
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                )}

                <div className={`text-[12px] p-2.5 rounded-lg ${
                  dueNoteContent.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : dueNoteContent.type === "warn"
                    ? "bg-amber-50 text-amber-800 border border-amber-200"
                    : dueNoteContent.type === "error"
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-gray-50 text-gray-500 border border-gray-200"
                }`}>
                  {dueNoteContent.text}
                </div>
              </div>

              {/* Purchase details + Notes */}
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[11px] text-gray-400 block mb-1">Purchase date</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-[7px] text-[12px] outline-none focus:border-blue-500"
                      value={purchaseDate}
                      onChange={(e) => {
                        if (paymentMode === "yes" && e.target.value > new Date().toISOString().split("T")[0]) {
                          toast.error("Future date not allowed for fully paid purchase")
                          return
                        }
                        setPurchaseDate(e.target.value)
                      }}
                      max={paymentMode === "yes" ? new Date().toISOString().split("T")[0] : undefined}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[11px] text-gray-400 block mb-1">Invoice / Bill no.</label>
                    <input
                      type="text"
                      placeholder="e.g., INV-2025-089"
                      className="w-full px-2 py-1.5 border border-gray-200 rounded-[7px] text-[12px] outline-none focus:border-blue-500"
                      value={invoiceNumber}
                      onChange={(e) => setInvoiceNumber(e.target.value)}
                    />
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Note for this purchase (optional)"
                  className="w-full px-2.5 py-2 border border-gray-200 rounded-[7px] text-xs outline-none focus:border-blue-500"
                  value={purchaseNotes}
                  onChange={(e) => setPurchaseNotes(e.target.value)}
                />
              </div>
            </div>

            {/* CTA */}
            <div className="p-4 border-t-[1.5px] border-gray-200 flex-shrink-0 bg-white">
              <button
                onClick={handleSubmit}
                disabled={submitting || cartItems.length === 0}
                className={`w-full rounded-[9px] py-3 px-3.5 text-[14.5px] font-bold flex items-center justify-center gap-2 transition-colors border-none cursor-pointer ${
                  submitting || cartItems.length === 0
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 text-white"
                }`}
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                Receive all items{totals.totalQty > 0 ? ` (${totals.totalQty} unit${totals.totalQty > 1 ? "s" : ""})` : ""}
              </button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BulkPurchaseDialog
